import React, {useRef} from 'react';
// Import Swiper
import {Swiper, SwiperSlide} from 'swiper/react';
import {Autoplay, EffectFade, Navigation, Pagination} from 'swiper/modules';
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

const slides = [
    {
        id: 1,
        headline: 'Rich Ingredients Real Goodness',
        description: 'Your familiar favorites products in bigger serving.',
        image: '/images/Hero1.png',
        imageMobile: '/images/Hero1-mobile.png',
        textColor: 'text-white',
        eyebrow: 'Taste of Tradition',
        textPosition: 'bottom'
    },
    {
        id: 2,
        headline: 'Thick, Creamy, Protein-Rich',
        description: 'Perfect for healthy breakfasts, fitness diets,<br /> and modern lifestyles.',
        image: '/images/Hero2.png',
        imageMobile: '/images/Hero2-mobile.png',
        textColor: 'text-white',
        eyebrow: 'Greek Yogurt',
        textPosition: 'top'
    },
    {
        id: 3,
        headline: 'The Chef’s Choice for Marinades & Dips',
        description: 'Ready-to-use Hung Curd made for creamy dips, kebab marinades, spreads, and healthy recipes.',
        image: '/images/Hero3.png',
        imageMobile: '/images/Hero3-mobile.png',
        textColor: 'text-white',
        eyebrow: 'Hung Curd',
        textPosition: 'top'
    },
    {
        id: 4,
        headline: 'Naturally Satisfying Dessert',
        description: 'Creamy fruit yogurt for a delicious,<br /> nutritious snack-ideal for guilt-free desserts<br /> and anytime healthy cravings.',
        image: '/images/Hero4.png',
        imageMobile: '/images/Hero4-mobile.png',
        textColor: 'text-white',
        eyebrow: 'Yogurt With Fruit',
        textPosition: 'bottom'
    },
    {
        id: 5,
        headline: 'A comforting Bowl',
        description: 'Smoothing goodness is perfect for lunch, dinner,<br /> or anytime comfort cravings.',
        image: '/images/curdrice.png',
        imageMobile: '/images/Hero5-mobile.png',
        textColor: 'text-white',
        eyebrow: 'Curd Rice',
        textPosition: 'bottom'
    }
];

const HeroSection = () => {
    const navigationPrevRef = useRef(null);
    const navigationNextRef = useRef(null);

    // Function to get text position classes
    const getTextPositionClass = (position) => {
        switch(position) {
            case 'top':
                return 'items-start pt-24 md:pt-32 lg:pt-40';
            case 'bottom':
                return 'items-end pb-24 md:pb-32 lg:pb-40';
            default:
                return 'items-center';
        }
    };

    return (
        <section id="hero" className="relative w-full h-screen overflow-hidden">
            <Swiper
                modules={[Navigation, Pagination, Autoplay, EffectFade]}
                effect="fade"
                fadeEffect={{ crossFade: true }}
                speed={1000}
                autoplay={{
                    delay: 5000,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true
                }}
                navigation={{
                    prevEl: navigationPrevRef.current,
                    nextEl: navigationNextRef.current,
                }}
                pagination={{
                    clickable: true,
                    type: 'bullets'
                }}
                loop={true}
                onInit={(swiper) => {
                    swiper.params.navigation.prevEl = navigationPrevRef.current;
                    swiper.params.navigation.nextEl = navigationNextRef.current;
                    swiper.navigation.init();
                    swiper.navigation.update();
                }}
                onSlideChange={(swiper) => {
                    // Reset all progress bars
                    document.querySelectorAll('.bullet-progress').forEach(el => {
                        el.style.width = '0%';
                    });
                    // Animate active bullet
                    setTimeout(() => {
                        const activeBullet = document.querySelector('.swiper-pagination-bullet-active .bullet-progress');
                        if (activeBullet) {
                            activeBullet.style.width = '100%';
                        }
                    }, 50);
                }}
                onAfterInit={(swiper) => {
                    // Animate first bullet
                    setTimeout(() => {
                        const activeBullet = document.querySelector('.swiper-pagination-bullet-active .bullet-progress');
                        if (activeBullet) {
                            activeBullet.style.width = '100%';
                        }
                    }, 50);
                }}
                className="w-full h-full"
            >
                {slides.map((slide) => (
                    <SwiperSlide key={slide.id}>
                        <div className="relative w-full h-full">
                            {/* Responsive Image */}
                            {/* Responsive Image - Mobile ONLY, Desktop ONLY */}
                            <picture className="absolute inset-0 w-full h-full">
                                {/* Desktop - large image */}
                                <source
                                    media="(min-width: 1024px)"
                                    srcSet={slide.image}
                                />
                                {/* Mobile - small image (YOUR EXISTING MOBILE IMAGES) */}
                                <source
                                    media="(max-width: 1023px)"
                                    srcSet={slide.imageMobile}
                                />
                                {/* Fallback */}
                                <img
                                    src={slide.image}
                                    alt={slide.headline}
                                    className="w-full h-full object-cover"
                                    loading="eager"
                                    fetchpriority="high"
                                />
                            </picture>

                            {/* Gradient Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />

                            {/* Text Content - with dynamic positioning */}
                            <div className={`absolute inset-0 flex ${getTextPositionClass(slide.textPosition)}`}>
                                <div className="max-w-7xl mx-auto px-6 w-full">
                                    <div className="max-w-xl">
                                        {/* Eyebrow */}
                                        {slide.eyebrow && (
                                            <h4 className="text-white/90 text-xs md:text-sm uppercase tracking-[0.2em] mb-3 font-semibold">
                                                {slide.eyebrow}
                                            </h4>
                                        )}

                                        {/* Headline - NO SUBHEADLINE SPAN */}
                                        <h1 className="text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-serif mb-3 leading-tight">
                                            {slide.headline}
                                        </h1>

                                        {/* Description */}
                                        <p
                                            className="text-white/80 text-sm sm:text-base md:text-lg mb-6 max-w-md leading-relaxed"
                                            dangerouslySetInnerHTML={{ __html: slide.description }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </SwiperSlide>
                ))}

                {/* Custom Navigation Arrows */}
                <button
                    ref={navigationPrevRef}
                    className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white transition-all flex items-center justify-center group"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="group-hover:scale-110 transition-transform">
                        <path d="M15 19L8 12L15 5" stroke="#1A3C34" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>

                <button
                    ref={navigationNextRef}
                    className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white transition-all flex items-center justify-center group"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="group-hover:scale-110 transition-transform">
                        <path d="M9 5L16 12L9 19" stroke="#1A3C34" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>

                {/* Pagination Container */}
                <div className="swiper-pagination !bottom-6 md:!bottom-8 flex justify-center gap-2"></div>
            </Swiper>

            {/* Add this style block once in your component or global CSS */}
            <style>{`
                .swiper-pagination-bullet {
                    width: 40px !important;
                    height: 4px !important;
                    background: rgba(255, 255, 255, 0.3) !important;
                    border-radius: 0 !important;
                    opacity: 1 !important;
                    position: relative;
                    overflow: hidden;
                }
                
                .swiper-pagination-bullet-active {
                    background: rgba(255, 255, 255, 0.3) !important;
                }
                
                .swiper-pagination-bullet-active .bullet-progress {
                    width: 100%;
                }
            `}</style>
        </section>
    );
};

export default HeroSection;